import { BackendError, ExchangeRateCountdown, isDefined } from '@eih-netbroker/common';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ReactComponent as EyeIcon } from '../../../assets/icons/eye-fill.svg';
import { ReactComponent as EyeStrikeIcon } from '../../../assets/icons/eye-strike-fill.svg';
import { useLocale } from '../../../context/NbLocalizationContext';
import { CodeInput, CodeInputHandle } from '../code-input/CodeInput';
import { CountdownBar } from '../countdown-bar/CountdownBar';
import { getRemainingSecondsLabel } from '../countdown-bar/countdownBarUtils';
import { NbIconButton } from '../icon-button/IconButton';
import { NbLoader } from '../loader/Loader';
import { ModalFooter } from '../modal/ModalFooter';
import { ModalHeader } from '../modal/ModalHeader';
import classes from './CodeInputModal.module.scss';

// Since domain specific information is presented on this modal, labels need to be customized.
// The optional ones provide a reasonable default, but the descriptions must be configured.
export type CodeInputModalLabels = {
  title?: string;
  inputLabel?: string;
  restartLabel?: string;
  cancelLabel?: string;
  submitLabel?: string;
  description: string;
};

export type CodeInputModalProps = {
  error?: BackendError;
  labels: CodeInputModalLabels;
  isInitializing: boolean;
  onSubmit: (text: string) => Promise<void>;
  onRestart: () => Promise<void>;
  onCancel: () => void;
  onClose: () => void;
  canRestart: (error: BackendError) => boolean;
  countdown?: ExchangeRateCountdown;
};

const codeLength = 4;

export const CodeInputModal = ({
  error,
  labels: _labels,
  isInitializing,
  onSubmit,
  onRestart,
  canRestart,
  onCancel,
  onClose,
  countdown,
}: CodeInputModalProps) => {
  const { translation } = useLocale();
  const [secureTextEntry, setSecureTextEntry] = useState(true);
  const [codeChars, setCodeChars] = useState<(string | undefined)[]>([]);
  const [isLoading, setLoading] = useState(false);
  const codeInputRef = useRef<CodeInputHandle>(null);

  const labels = useMemo<Required<CodeInputModalLabels>>(
    () => ({
      title: translation.codeInputModal.defaultTitle,
      inputLabel: translation.codeInputModal.defaultInputLabel,
      restartLabel: translation.codeInputModal.defaultRestartLabel,
      cancelLabel: translation.general.cancel,
      submitLabel: translation.general.send,
      ..._labels,
    }),
    [_labels, translation],
  );

  const code = useMemo<string | undefined>(
    () => (codeChars.length === codeLength && codeChars.every(isDefined) ? codeChars.join('') : undefined),
    [codeChars],
  );

  const toggleSecureTextEntry = useCallback(() => setSecureTextEntry(s => !s), []);

  const handleOnConfirmPress = useCallback(async () => {
    if (isDefined(code)) {
      try {
        setLoading(true);
        await onSubmit(code);
      } finally {
        setLoading(false);
      }
      // If after submit we are still open, we reset the chars and the focus.
      setCodeChars([]);
      codeInputRef.current?.focus?.(0);
    }
  }, [code, onSubmit]);

  const handleOnRestartPress = useCallback(async () => {
    codeInputRef.current?.blur();
    setCodeChars([]);
    try {
      setLoading(true);
      await onRestart();
    } finally {
      setLoading(false);
    }
    codeInputRef.current?.focus(0);
  }, [onRestart]);

  const handleOnChangeCode = useCallback((codeChars: (string | undefined)[]) => {
    setCodeChars(codeChars);
  }, []);

  const isCodeIncomplete = code === undefined;
  const isRestartable = isDefined(error) && canRestart(error);
  const SecureEntryIcon = secureTextEntry ? EyeIcon : EyeStrikeIcon;

  return (
    <div>
      <ModalHeader
        title={labels.title}
        className={classes.header}
        onClose={onClose}
        hideCloseButton={isLoading}
      />
      <div className={classes.contentContainer}>
        {isInitializing && <NbLoader horizontalCenter />}
        {!isInitializing && (
          <>
            {!isRestartable && <p className={classes.description}>{labels.description}</p>}
            {isRestartable && <p className={classes.description}>{error.errorMessage}</p>}
            {!isRestartable && (
              <div className={classes.codeWithLabel}>
                <label className={classes.codeLabel}>{labels.inputLabel}</label>
                <div className={classes.codeWithVisibilityButton}>
                  <CodeInput
                    codeLength={codeLength}
                    secureTextEntry={secureTextEntry}
                    value={codeChars}
                    autoFocus={true}
                    ref={codeInputRef}
                    disabled={isLoading}
                    onChange={handleOnChangeCode}
                    onSubmit={handleOnConfirmPress}
                  />
                  <NbIconButton
                    icon={<SecureEntryIcon className={classes.visibilityIcon} />}
                    onClick={toggleSecureTextEntry}
                  />
                </div>
              </div>
            )}
            {isDefined(error) && !isRestartable && <span className={classes.error}>{error.errorMessage}</span>}
          </>
        )}
        {isDefined(countdown) && countdown !== 'indefinite' && !isRestartable && (
          <CountdownBar
            max={countdown.durationSeconds}
            remaining={countdown.secondsRemaining}
            getLabel={getRemainingSecondsLabel}
            shouldUseCountdownBarPlaceholder={false}
          />
        )}
      </div>
      <ModalFooter
        className={classes.footer}
        left={{
          title: labels.cancelLabel,
          action: onCancel,
          disabled: isLoading,
          variant: 'outlined',
          color: 'primary',
        }}
        right={{
          title: isRestartable ? labels.restartLabel : labels.submitLabel,
          action: isRestartable ? handleOnRestartPress : handleOnConfirmPress,
          disabled: (!isRestartable && isCodeIncomplete) || isInitializing,
          isLoading,
        }}
      />
    </div>
  );
};
